import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { HeaderSearchInitiate, HeaderSearchComplete, sendGA4Data } from '../../analytics/events';
import { SPA_ROUTE_EVENTS, useSpaRouteEvents } from '../../hooks/useSpaRouteEvents';
import { loadSearchBarComponent } from '../remotes/enterpriseSearchApp';
import { buildSearchUrl } from '@headless-shell/shared-components';
import { isValidElementType } from 'react-is';
import {
  SearchBarContainer,
  getMobileInputStyles,
  mobileOverlayStyles,
  getMobilePaneStyles,
  mobileBackContainerStyles,
  mobileListboxStyles,
} from './styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { breakpoints } from '@headless-shell/shared-styles/src/styles/breakpoints';

const STYLE_RETRY_DELAY_MS = 50;
const STYLE_RETRY_ATTEMPTS = 12;

class SearchBarErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const ownerStack =
      process.env.NODE_ENV !== 'production' &&
      typeof React.captureOwnerStack === 'function'
        ? React.captureOwnerStack()
        : null;

    console.error('GlobalSearch SearchBar crashed:', {
      error,
      componentStack: info?.componentStack,
      ownerStack,
    });
  }

  render() {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}

SearchBarErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
};

const GlobalSearch = memo(({ navigate, onSearchComplete, width , isAuthenticated, onClose = () => {} }) => {
  const [SearchBar, setSearchBar] = useState(null);
  const containerRef = useRef(null);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.smMin})`);
  const getSearchQueryFromUrl = useCallback(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    return hashParams.get('q') || '';
  }, []);

  const [searchQuery, setSearchQuery] = useState(getSearchQueryFromUrl());
  const syncSearchQueryFromUrl = useCallback(() => {
    setSearchQuery(getSearchQueryFromUrl());
  }, [getSearchQueryFromUrl]);

  useSpaRouteEvents(syncSearchQueryFromUrl, {
    events: [...SPA_ROUTE_EVENTS, 'hashchange'],
  });

  const handleSearchInitiate = useCallback(() => {
    sendGA4Data(HeaderSearchInitiate);
  }, []);

  // Load SearchBar component
  useEffect(() => {
    loadSearchBarComponent()
      .then((component) => {
        if (!isValidElementType(component)) {
          throw new Error(
            `Loaded enterprise search SearchBar is not a valid React component type (got: ${typeof component})`
          );
        }
        setSearchBar(() => component);
      })
      .catch((err) => {
        console.error('Failed to load SearchBar:', err);
        setSearchBar(null);
      });
  }, []);

  useEffect(() => {
    if (!SearchBar || !containerRef.current) return;

    const container = containerRef.current;
    const getBar = () => container.querySelector('#enterprise-search-bar');

    const resetMobileStyles = () => {
      const bar = getBar();
      if (!bar?.shadowRoot) return;
      ['sdf-input', 'div', 'sdf-floating-pane', '.back-container', 'sdf-select-item-list'].forEach((sel) =>
        bar.shadowRoot.querySelector(sel)?.removeAttribute('style')
      );
    };

    if (!isMobile) {
      resetMobileStyles();
      return;
    }

    const retryTimers = [];

    const applyInputStyles = () => {
      const bar = getBar();
      if (!bar?.shadowRoot) return false;

      const input = bar.shadowRoot.querySelector('sdf-input');
      if (input) Object.assign(input.style, getMobileInputStyles(isAuthenticated));

      const overlay = bar.shadowRoot.querySelector('div');
      if (overlay) Object.assign(overlay.style, mobileOverlayStyles);
      return Boolean(input);
    };

    const applyPaneStyles = () => {
      const bar = getBar();
      if (!bar?.shadowRoot) return false;

      const pane = bar.shadowRoot.querySelector('sdf-floating-pane');
      if (!pane) return false;

      Object.assign(pane.style, getMobilePaneStyles(isAuthenticated));

      const backContainer = pane.querySelector('.back-container');
      if (backContainer) Object.assign(backContainer.style, mobileBackContainerStyles);

      const listbox = pane.querySelector('sdf-select-item-list');
      if (listbox) Object.assign(listbox.style, mobileListboxStyles);

      return true;
    };

    const applySearchStyles = () => applyInputStyles() && applyPaneStyles();

    const scheduleStyleApplication = (attempt = 0) => {
      if (applySearchStyles() || attempt >= STYLE_RETRY_ATTEMPTS) return;

      const timer = window.setTimeout(() => {
        scheduleStyleApplication(attempt + 1);
      }, STYLE_RETRY_DELAY_MS);

      retryTimers.push(timer);
    };

    // Wrap so the browser Event object is never passed as `attempt`
    const onInteraction = () => scheduleStyleApplication();
    
    scheduleStyleApplication();

    container.addEventListener('click', onInteraction);
    container.addEventListener('focusin', onInteraction);

    return () => {
      retryTimers.forEach((timer) => window.clearTimeout(timer));
      container.removeEventListener('click', onInteraction);
      container.removeEventListener('focusin', onInteraction);
      resetMobileStyles();
    };
  }, [isMobile, SearchBar, isAuthenticated]);

  const handleSearchSubmit = useCallback((searchValue, hashParam = '', _searchPath, metadata = {}) => {
    setSearchQuery(searchValue);

    const targetUrl = buildSearchUrl({ searchValue, hashParam, isAuthenticated });
    navigate(targetUrl);
    onClose();
    const searchType = metadata?.searchType === 'recommended' ? 'recommended' : 'manual';
    sendGA4Data(HeaderSearchComplete, { eventLabel: searchValue, search_type: searchType });
    onSearchComplete?.();
  }, [isAuthenticated, navigate, onSearchComplete, onClose]);

  if (!SearchBar) {
    // If the remote isn't available (bad URL/cert/404) or exports are incorrect,
    // don't crash the entire shell; just omit the global search UI.
    return null;
  }

  return (
  <SearchBarContainer ref={containerRef} id="headless-search">
    {SearchBar && (
    <SearchBarErrorBoundary fallback={null}>
      <SearchBar
        key={searchQuery}
        initialValue={searchQuery}
        width={width}
        useRouterNavigation={true}
        navigate={navigate}
        placeholder="Search"
        onSearchInitiate={handleSearchInitiate}
        onSearch={handleSearchSubmit}
        showButton={false}
        documentType="MKP"
        searchPath="/listing"
      />
    </SearchBarErrorBoundary>
    )}
  </SearchBarContainer>
);
});

GlobalSearch.displayName = 'GlobalSearch';

GlobalSearch.propTypes = {
  navigate: PropTypes.func.isRequired,
  onSearchComplete: PropTypes.func,
  width: PropTypes.string,
  isAuthenticated: PropTypes.bool,
};

GlobalSearch.defaultProps = {
  onSearchComplete: undefined,
  width: 'w-full',
  isAuthenticated: false,
};

export default GlobalSearch;
